import { inject, Injectable, Injector } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'

import { SelectItem } from 'primeng/api'
import { BehaviorSubject, combineLatest, map, takeUntil } from 'rxjs'

import {
  AppFeatures,
  CountryCodes,
  FLEET_CUSTOM_ROLE_ID_RANGE,
  TSP_CUSTOM_ROLE_ID_RANGE,
  UserAuthMethod,
  UserProfile,
  UserRole,
} from '@ti/contracts'
import { Profile } from '@ti/core/auth/services/my.profile'
import { isTspRole } from '@ti/core/auth/utils'
import { PasswordValidator } from '@ti/core/auth/utils/password-validator'
import { Memoize } from '@ti/core/common/decorators'
import { PricingPlanService, WhiteLabelSettingsProvider } from '@ti/core/common/services'
import {
  completeStore,
  exposeReadonlyState,
  formState,
  injectDestroy$,
  isGroupRestricted,
  toSignalState,
} from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { LanguageService, phoneNumberValidator } from '@ti/core/ui-kit/i18n'
import { UserRolesProvider } from '@ti/shared/roles/providers'
import { RoleStore } from '@ti/shared/roles/services/role.store'

export enum UserFormMode {
  TI = 'TI',
  TSP = 'TSP',
  Fleet = 'Fleet',
}

@Injectable()
export class UserFormModel {
  protected readonly config = inject(CONFIG)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly injector = inject(Injector)
  protected readonly languageService = inject(LanguageService)
  protected readonly pricingPlanService = inject(PricingPlanService)
  protected readonly profile = inject(Profile)
  protected readonly userOptionsProvider = inject(UserRolesProvider)
  protected readonly whiteLabelProvider = inject(WhiteLabelSettingsProvider)
  protected readonly roleStore = inject(RoleStore)

  protected readonly destroy$ = injectDestroy$()
  // Group access of the current (managing) user; null when unrestricted
  protected managerGroupsAccess: string[] | null = null
  // True when the current user is group-restricted and manages fleet users with groups enabled
  protected groupsAccessRequired = false
  protected readonly store$ = {
    user: new BehaviorSubject<Partial<UserProfile>>({}),
    mode: new BehaviorSubject<UserFormMode>(UserFormMode.Fleet),
    forceRole: new BehaviorSubject<UserRole | undefined>(undefined),
    country: new BehaviorSubject<CountryCodes>(CountryCodes.UnitedStates),
    authMethodUsername: new BehaviorSubject<boolean>(false),
    emailError: new BehaviorSubject<string>(''),
    usernameError: new BehaviorSubject<string>(''),
    emptyGroupsListWarningDisplayed: new BehaviorSubject<boolean>(false),
    isLoading: new BehaviorSubject<boolean>(false),
  }
  public readonly userForm = new FormGroup({
    id: new FormControl<string>(''),
    name: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [
      (control: AbstractControl) => {
        if (this.store$?.authMethodUsername.value) return null
        return Validators.required(control)
      },
      Validators.email,
    ]),
    phoneNumber: new FormControl<string>('', [phoneNumberValidator]),
    roleId: new FormControl<number | undefined>(undefined, [Validators.required]),
    groupsAccess: new FormControl<string[] | null>(null, [
      (control: AbstractControl) => {
        // A group-restricted user must assign at least one of their own groups
        if (!this.groupsAccessRequired) return null
        return Validators.required(control)
      },
    ]),
  })
  public readonly credentialsForm = new FormGroup({
    username: new FormControl<string>('', [
      (control) => {
        if (this.store$?.user?.value?.id) return null
        return Validators.required(control)
      },
    ]),
    password: new FormControl<string>('', [
      (control) => {
        if (this.store$?.user?.value?.id) return null
        return Validators.required(control)
      },
      Validators.minLength(8),
      PasswordValidator,
    ]),
  })

  // For templates of components that read the forms but do not own them
  public readonly userFormState = formState(this.userForm, this.injector)
  public readonly credentialsFormState = formState(this.credentialsForm, this.injector)

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((e) => console.error('Cannot init UserFormModel', e))

    this.destroy$.subscribe(() => completeStore(this.store$))
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      ...exposeReadonlyState(this.store$, [
        'user',
        'mode',
        'forceRole',
        'country',
        'authMethodUsername',
        'emailError',
        'usernameError',
        'isLoading',
        'emptyGroupsListWarningDisplayed',
      ]),
      groupsAndLabelsAllowed: this.pricingPlanService.areFeaturesAllowed$([
        AppFeatures.GroupsAndLabels,
      ]),
      adminRoleOptions: this.createAdminRoleOptionsStream(),
      tspRoleOptions: this.createTspRoleOptionsStream(),
      fleetRoleOptions: this.createFleetRoleOptionsStream(),
    }
  }

  public set data(value: Partial<UserProfile>) {
    this.store$.user.next(value)
    this.store$.authMethodUsername.next(value.authMethod === UserAuthMethod.Username)

    // Hide auto-generated backend emails from the UI
    const displayEmail = value.email?.includes('@telematicsalerts.com') ? '' : value.email || ''

    // Wait until patching forms to ensure credentials is rendered
    setTimeout(() => {
      this.userForm.patchValue({
        id: value.id || '',
        name: value.name || '',
        email: displayEmail,
        phoneNumber: value.phoneNumber || '',
        roleId: value.roleId || null,
        // Pre-populate a new user with all groups of a group-restricted manager
        groupsAccess:
          value.groupsAccess ||
          (!value.id && this.groupsAccessRequired ? [...(this.managerGroupsAccess ?? [])] : null),
      })

      this.credentialsForm.patchValue({
        username: value.username ?? '',
        password: '',
      })

      // Disable username control for editing existing user
      if (value.id && !this.credentialsForm.controls.username.disabled) {
        this.credentialsForm.controls.username.disable()
      }
      if (!value.id && this.credentialsForm.controls.username.disabled) {
        this.credentialsForm.controls.username.enable()
      }

      if (value.roleId === UserRole.TSPOwner || value.roleId === UserRole.FleetOwner) {
        this.userForm.controls.roleId.disable({ emitEvent: false })
      } else {
        this.userForm.controls.roleId.enable({ emitEvent: false })
      }

      this.userForm.updateValueAndValidity()
      this.credentialsForm.updateValueAndValidity()
    }, 16)
  }

  public set country(value: CountryCodes) {
    this.store$.country.next(value)
  }

  public set mode(value: UserFormMode) {
    this.store$.mode.next(value)
  }

  public set forceRole(value: UserRole | undefined) {
    this.store$.forceRole.next(value)
  }

  public get isValid() {
    return (
      this.userFormState.valid() &&
      (!this.state.authMethodUsername() || this.credentialsFormState.valid()) &&
      !this.state.emailError() &&
      !this.state.usernameError()
    )
  }

  @Memoize()
  get predefinedTspRoleOptions$() {
    return combineLatest([
      this.profile.state,
      this.userOptionsProvider.userRoleLabels$,
      this.store$.user,
      this.store$.forceRole,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([currentUser, labels, editedUser, forceRole]) => {
        if (forceRole) {
          return [{ label: labels[forceRole], value: forceRole }]
        }

        const data: SelectItem[] = []
        if (editedUser.roleId === UserRole.TSPOwner) {
          data.push({ label: labels[UserRole.TSPOwner], value: UserRole.TSPOwner })
        }
        if (currentUser.roleId === UserRole.TSPOwner || editedUser.roleId === UserRole.TSPAdmin) {
          data.push({ label: labels[UserRole.TSPAdmin], value: UserRole.TSPAdmin })
        }
        if (currentUser.roleId === UserRole.TSPOwner || editedUser.roleId === UserRole.TSPFinance) {
          data.push({ label: labels[UserRole.TSPFinance], value: UserRole.TSPFinance })
        }
        return data.concat([
          { label: labels[UserRole.TSPManager], value: UserRole.TSPManager },
          { label: labels[UserRole.TSPSupport], value: UserRole.TSPSupport },
          { label: labels[UserRole.TSPViewer], value: UserRole.TSPViewer },
        ])
      }),
    )
  }

  @Memoize()
  get predefinedFleetRoleOptions$() {
    return combineLatest([
      this.profile.state,
      this.userOptionsProvider.userRoleLabels$,
      this.store$.user,
      this.store$.forceRole,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([currentUser, labels, editedUser, forceRole]) => {
        if (forceRole) {
          return [{ label: labels[forceRole], value: forceRole }]
        }

        const data: SelectItem[] = []
        if (editedUser.roleId === UserRole.FleetOwner) {
          data.push({ label: labels[UserRole.FleetOwner], value: UserRole.FleetOwner })
        }
        if (
          editedUser.roleId === UserRole.FleetAdmin ||
          isTspRole(currentUser.roleId) ||
          currentUser.roleId === UserRole.FleetOwner ||
          currentUser.roleId === UserRole.FleetDemo
        ) {
          data.push({ label: labels[UserRole.FleetAdmin], value: UserRole.FleetAdmin })
        }
        return data.concat([
          // FleetInstaller disabled per comment
          { label: labels[UserRole.FleetMember], value: UserRole.FleetMember },
          { label: labels[UserRole.FleetViewer], value: UserRole.FleetViewer },
        ])
      }),
    )
  }

  public setEmailError(value: string) {
    this.store$.emailError.next(value)
  }

  public setUsernameError(value: string) {
    this.store$.usernameError.next(value)
  }

  public setAuthMethodUsernameEnabled(value: boolean) {
    this.store$.authMethodUsername.next(value)
  }

  public setIsLoading(value: boolean) {
    this.store$.isLoading.next(value)
  }

  public reset() {
    // Reset forms
    this.userForm.reset()
    this.credentialsForm.reset()

    // Reset store
    this.store$.user.next({})
    this.store$.country.next(CountryCodes.UnitedStates)
    this.store$.authMethodUsername.next(false)
    this.store$.emailError.next('')
    this.store$.usernameError.next('')
    this.store$.emptyGroupsListWarningDisplayed.next(false)
    this.store$.isLoading.next(false)

    this.applyDefaultGroupsAccess()
  }

  public getData() {
    const value = this.userForm.getRawValue()
    const original = this.store$.user.value

    const credentials = this.store$.authMethodUsername.value
      ? this.credentialsForm.getRawValue()
      : ({} as any)

    const data: Partial<UserProfile> & {
      username?: string
      password?: string
    } = {
      ...value,
      ...credentials,
      phoneNumber: value.phoneNumber ? value.phoneNumber.replace(/[^0-9]/g, '') : '',
    }

    if (original?.id && original?.email?.includes('@telematicsalerts.com') && !value?.email) {
      delete data.email
    }

    return data
  }

  public async openCopyCredentialsDialog(data: { username?: string; password?: string }) {
    // TODO: Move this translations to the user section
    const labels = await this.languageService.massTranslate({
      summary: 'users.copy-credentials-dialog.summary',
      description: 'users.copy-credentials-dialog.description',
      tooltip: 'users.copy-credentials-dialog.tooltip',
      username: 'users.copy-credentials-dialog.username',
      password: 'users.copy-credentials-dialog.password',
      appUrl: 'users.copy-credentials-dialog.app-url',
    })
    const whiteLabelSettings = await this.whiteLabelProvider.getData()

    await this.dialogFacade.copy({
      summary: labels.summary,
      description: labels.description,
      copyTooltip: labels.tooltip,
      data: [
        {
          key: labels.appUrl,
          value:
            this.state.mode() === UserFormMode.Fleet
              ? whiteLabelSettings.domain || this.config.fleetDefaultDomain
              : location.origin,
        },
        { key: labels.username, value: data.username || '' },
        { key: labels.password, value: data.password || '' },
      ],
    })
  }

  public cancel() {
    this.userForm.reset()
    this.credentialsForm.reset()

    this.applyDefaultGroupsAccess()
  }

  // Pre-populate a new user with all groups of a group-restricted manager
  protected applyDefaultGroupsAccess() {
    if (!this.store$.user.value?.id && this.groupsAccessRequired) {
      this.userForm.controls.groupsAccess.setValue([...(this.managerGroupsAccess ?? [])], {
        emitEvent: false,
      })
    }
  }

  protected init() {
    // Clear email error after field is updated
    this.userForm.controls.email.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setEmailError(''))

    // Clear username error after field is updated
    this.credentialsForm.controls.username.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.setUsernameError(''))

    // A group-restricted user may manage fleet users but must assign at least one group
    combineLatest([
      this.profile.state,
      this.store$.mode,
      this.pricingPlanService.areFeaturesAllowed$([AppFeatures.GroupsAndLabels]),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, mode, groupsAllowed]) => {
        this.managerGroupsAccess = user?.groupsAccess ?? null
        this.groupsAccessRequired =
          mode === UserFormMode.Fleet && groupsAllowed && isGroupRestricted(user?.groupsAccess)
        this.userForm.controls.groupsAccess.updateValueAndValidity({ emitEvent: false })
        this.userFormState.sync()
      })

    // Empty groups list warning
    this.userForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const data = this.store$.user.value
      const display = Boolean(
        Array.isArray(data.groupsAccess) &&
        data.groupsAccess.length === 0 &&
        value.groupsAccess?.length === 0,
      )
      this.store$.emptyGroupsListWarningDisplayed.next(display)
    })

    // Credentials visibility/validation changes
    this.store$.authMethodUsername.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.credentialsForm.reset()
      // trigger validation updates for email control
      this.userForm.controls.email.updateValueAndValidity({ emitEvent: false })
      this.userFormState.sync()
    })
  }

  protected createAdminRoleOptionsStream() {
    return combineLatest([
      this.profile.state,
      this.userOptionsProvider.userRoleLabels$,
      this.store$.user,
      this.store$.forceRole,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([currentUser, labels, editedUser, forceRole]) => {
        if (forceRole) {
          return [{ label: labels[forceRole], value: forceRole }]
        }

        const data: SelectItem[] = []
        if (editedUser.roleId === UserRole.TIOwner) {
          data.push({ label: labels[UserRole.TIOwner], value: UserRole.TIOwner })
        }
        if (currentUser.roleId === UserRole.TIOwner || editedUser.roleId === UserRole.TIAdmin) {
          data.push({ label: labels[UserRole.TIAdmin], value: UserRole.TIAdmin })
        }
        if (currentUser.roleId === UserRole.TIOwner || editedUser.roleId === UserRole.TIFinance) {
          data.push({ label: labels[UserRole.TIFinance], value: UserRole.TIFinance })
        }
        return data.concat([
          { label: labels[UserRole.TISupport], value: UserRole.TISupport },
          { label: labels[UserRole.TIViewer], value: UserRole.TIViewer },
          { label: labels[UserRole.TIDeveloper], value: UserRole.TIDeveloper },
        ])
      }),
    )
  }

  protected createFleetRoleOptionsStream() {
    return combineLatest([this.predefinedFleetRoleOptions$, this.roleStore.state$.items]).pipe(
      takeUntil(this.destroy$),
      map(([labels, roles]) => {
        const customRoleOptions: SelectItem[] = roles
          .filter(
            (role) =>
              role.id >= FLEET_CUSTOM_ROLE_ID_RANGE[0] && role.id <= FLEET_CUSTOM_ROLE_ID_RANGE[1],
          )
          .map((role) => ({
            label: role.name,
            value: role.id,
            isCustom: true,
          }))

        return (
          labels
            .concat(customRoleOptions)
            // Sort alphabetically
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
        )
      }),
    )
  }

  protected createTspRoleOptionsStream() {
    return combineLatest([this.predefinedTspRoleOptions$, this.roleStore.state$.items]).pipe(
      takeUntil(this.destroy$),
      map(([builtInRoleOptions, roles]) => {
        const customRoleOptions: SelectItem[] = roles
          .filter(
            (role) =>
              role.id >= TSP_CUSTOM_ROLE_ID_RANGE[0] && role.id <= TSP_CUSTOM_ROLE_ID_RANGE[1],
          )
          .map((role) => ({
            label: role.name,
            value: role.id,
            isCustom: true,
          }))

        return (
          builtInRoleOptions
            .concat(customRoleOptions)
            // Sort alphabetically
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
        )
      }),
    )
  }
}
